import { SpecialScriptDef } from '../types';

/**
 * 特殊剧的「定角夜」所在月＝附表窗口末季的末月（内部绝对月口径 `year*12+month`，month 1..12）。
 * 2026-10-14 茄子定「七月试镜也是十二月底出结果」：玩家随时能去试镜，但竞角结果与签约压到这一夜，
 * 普通 NPC 也在同一夜接剩下的位，全组下一季开工——接与不接同一定角夜、同一月开机
 * （《颠倒姻缘》窗口 Y1Q2~Y1Q4，定角夜＝第 1 年 12 月，开机＝第 2 年 1 月）。
 * @returns 非特殊剧本表里的剧 / 窗口为空则返回 null（按普通剧处理）
 */
export function specialDeadlineAbs(scriptId: string): number | null {
    const sp = SPECIAL_SCRIPTS.find(s => s.scriptId === scriptId);
    if (!sp) return null;
    let best = -1;
    for (const season of sp.availableSeasons) {
        const m = /^Y(\d+)Q(\d+)$/.exec(season);
        if (!m) continue;
        const abs = (Number(m[1]) - 1) * 12 + Number(m[2]) * 3;   // 末季末月：Q4 记 12、Q1 记 3
        if (abs > best) best = abs;
    }
    return best > 0 ? best : null;
}

/**
 * 这一夜的竞角结果该不该端出来（5c 的唯一一把尺）。
 * 普通剧随时可出；特殊剧压到定角夜那一季（见上「七月试镜也是十二月底出结果」）。
 * 开页判定（ScriptSystem.hasPendingReveals）与结果名单（revealAuditions）不许各写一遍：
 * 2026-10-14 实测空页 bug（第 1 年 9 月收口夜，货架上没有到期剧、只有未到定角夜的特殊剧资格，
 * 判定说「有待揭晓」、名单却是空的，玩家看见一张没有角色的经纪人聊天页）就是这么来的。
 * 反向用法（换新/收口时「这条资格留着压后」）＝ `!revealDueNow(...)`，同一条筛不许两边各拼。
 */
export function revealDueNow(scriptId: string, absNow: number): boolean {
    const deadline = specialDeadlineAbs(scriptId);
    return deadline === null || deadline <= absNow;
}

/**
 * 特殊剧本附表（2026-10-06 定稿，设计基准 docs/特殊剧本实装设计.md）
 *
 * 只放特殊专用字段；剧本内容本体在普通剧本表各分片（SCRIPT_901 → web.ts、902/904 → tv-guzhuang.ts、
 * 903 → movie-xiaochengben.ts），以 scriptId 一对一关联。
 *
 * 三类判别：有 availableSeasons 无 unlockFlag = 定时上架型（到点上架全服可见）；
 * 两者都有 = 剧情解锁型（flag 置位前对玩家不存在）。
 *
 * plotHooks：剧本生命周期（cast 进组 / done 杀青 / airStart 开播 / rating 评分揭晓）
 * → 触发 FixedEvents 表事件。默认过时不候（到期 cond 不满足作废），wait: true 才挂起。
 * 播出侧两个时点（2026-10-14 茄子定）：`airStart`（开播，原名 airBoom「热爆」，同日改名——名字说的「爆线达成」与时点「开播当月月末」对不上）发在**开播当月月末**＝播出第一个月，不等播完——
 * 帖文写的是「开播三天/上线一周」，压到播完月就成了马后炮；`rating`（评分揭晓）发在**播出完结那个月月末**
 * ＝长尾期第一个月（履历行 tailK=1 那个月），两条不是一个时点。入队后都落在下个月的决策页演
 * （`FixedEventDef.playOn='decision'`）。
 * 现网一条 cond 都没有（2026-10-14 茄子令全撤）：那四道 heat/rating 是批 2 起草数据结构时从热度榜爆线
 * 抄来的占位（901 底分 100、903 底分 8.4 本就恒真，只有 902/904 真在拦），而承天/无此镇/十二阶的剧情文案
 * 一句还没写。要补门槛等文案就位、按当条剧情的意思定线，不许提前替玩家决定「这戏扑了就不演舆论」。
 * 事件本体（文案/选项/media 评论池）批 2 建 FixedEvents.ts 时落，此处先登记事件 ID。
 *
 * 数值均为暂定占位，最终由茄子手动设定。
 * ⚠️ 《平城雪》（后期剧，编号待定）暂不进本表，见 docs/剧本/特殊剧本/README.md。
 */
export const SPECIAL_SCRIPTS: SpecialScriptDef[] = [
    {
        // 颠倒姻缘（剧情解锁型）：外出偶遇导演许林辉（FixedEvents 偶遇事件 unlockScripts 置位）后才存在
        scriptId: 'SCRIPT_901',
        availableSeasons: ['Y1Q2', 'Y1Q3', 'Y1Q4'],
        unlockFlag: 'PLOT_UNLOCK_SCRIPT_901',
        // 竞角必得位（2026-10-14 茄子点名）：柳惊蛰（女主角）与李令仪（女二号）试镜一过就不掷骰，直接记「竞得」。
        // 剧情上讲得通——《颠倒姻缘》这本子就是许林辉冲着玩家写的，女主与女二两个位子给他留着。
        // 只管竞角那一道掷点：试镜门槛、定角夜、NPC 侧收口派角、存档版本四件事一律照旧。
        autoWinRoles: ['柳惊蛰', '李令仪'],
        plotHooks: [
            // 进组演片场开拍第一周对话；杀青演片场夜对话；开播演"穷网剧逆袭"讨论贴加新闻加私信（10-14 由 airBoom 改名）
            { when: 'cast', event: 'FIXED_901_SHOOT' },
            { when: 'done', event: 'FIXED_901_DONE_NIGHT' },
            { when: 'airStart', event: 'FIXED_901_BOOM' },
        ],
    },
    {
        // 承天（定时上架型）：Y1Q3 到点上架全服可见，杀青偶遇结识庄文茵（relatedNPCs 偶遇池）
        scriptId: 'SCRIPT_902',
        availableSeasons: ['Y1Q3', 'Y1Q4', 'Y2Q1'],
        plotHooks: [
            // 进组演片场初遇（庄文茵教走位，结识）；开播演"影后演少女"群嘲热帖＋新闻卡
            // （2026-10-19 落文案，源文档 docs/特殊剧情/承天.md）
            { when: 'cast', event: 'FIXED_902_MEET' },
            { when: 'airStart', event: 'FIXED_902_BOOM' },
        ],
    },
    {
        // 无此镇（定时上架型·高口碑）：Y2 起上架，口碑黑马
        scriptId: 'SCRIPT_903',
        availableSeasons: ['Y2Q2', 'Y2Q3', 'Y2Q4', 'Y3Q1', 'Y3Q2'],
        plotHooks: [],      // 同上：评分揭晓那条占位帖与钩子一并删除
    },
    {
        // 十二阶（剧情解锁型·定稿 2026-10-06）：穆遥递邀约（FixedEvents 事件 unlockScripts 置位）
        scriptId: 'SCRIPT_904',
        availableSeasons: ['Y3Q1', 'Y3Q2', 'Y3Q3', 'Y3Q4'],
        unlockFlag: 'PLOT_UNLOCK_SCRIPT_904',
        plotHooks: [],      // 同上：杀青/热爆两条占位与钩子一并删除
    },
];
